import React, { useEffect, useRef } from 'react';
import { refreshToken } from 'services/authServices';
import { useSessionStore } from 'services/sessionStore';

const UserActivityTracker = () => {
  // Configuration - 30 minute session
  const SESSION_DURATION = 30 * 60 * 1000;
  
  // Track last activity and refresh times
  const lastActivityRef = useRef(Date.now());
  const timerRef = useRef(null);
  
  // Main refresh function
  const refreshUserSession = async () => {
    if (!localStorage.getItem('token')) return;
    
    try {
      await refreshToken();
      scheduleRefresh();
    } catch (error) {
      console.error('Token refresh failed:', error);
      useSessionStore.setState({ isSessionExpired: true });
    }
  };
  
  // Schedule next refresh
  const scheduleRefresh = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    
    // Refresh when session is 80% complete
    const refreshTime = SESSION_DURATION * 0.8;
    
    // Calculate delay - ensuring we don't get negative values
    const timeSinceLastActivity = Date.now() - lastActivityRef.current;
    const delay = Math.max(refreshTime - timeSinceLastActivity, 0);
    
    timerRef.current = setTimeout(() => {
      if (Date.now() - lastActivityRef.current >= refreshTime) {
        useSessionStore.setState({ isSessionExpired: true });
      } else {
        refreshUserSession();
      }
    }, delay);
  };
  
  // Handle user activity
  const handleUserActivity = () => {
    lastActivityRef.current = Date.now();
  };
  
  useEffect(() => {
    // Events to track
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    
    // Throttle events
    let throttleTimer;
    const throttledHandler = () => {
      if (!throttleTimer) {
        throttleTimer = setTimeout(() => {
          handleUserActivity();
          throttleTimer = null;
        }, 1000);
      }
    };
    
    // Add listeners
    events.forEach(event => {
      window.addEventListener(event, throttledHandler, { passive: true });
    });
    
    // Initial setup
    scheduleRefresh();
    
    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, throttledHandler);
      });
      clearTimeout(throttleTimer);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);
  
  return null;
};

export default UserActivityTracker; 